import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, applyNodeChanges, applyEdgeChanges,
  Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Workflow, Zap, GitBranch } from 'lucide-react';
import api from '../../services/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';

const NODE_COLORS = {
  trigger: '#10b981',
  condition: '#8b5cf6',
  action_whatsapp: '#25D366',
  action_sms: '#f59e0b',
  action_email: '#3b82f6',
  action_webhook: '#6366f1',
  action_ai_call: '#ec4899',
  action_tag: '#f97316',
  action_assign: '#64748b',
  action_create_lead: '#0ea5e9',
  action_assign_department: '#7c3aed',
  action_create_task: '#14b8a6',
  action_notify_user: '#eab308',
  action_create_support_ticket: '#dc2626',
  delay: '#94a3b8',
  end: '#ef4444',
};

const NODE_LABELS = {
  trigger: 'Trigger', condition: 'Condition',
  action_whatsapp: 'WhatsApp', action_sms: 'SMS',
  action_email: 'Email', action_webhook: 'Webhook',
  action_ai_call: 'AI Call', action_tag: 'Add Tag',
  action_assign: 'Assign',
  action_create_lead: 'Create Lead',
  action_assign_department: 'Assign Department',
  action_create_task: 'Create Task',
  action_notify_user: 'Notify User',
  action_create_support_ticket: 'Create Support Ticket',
  delay: 'Delay', end: 'End',
};

function WFNode({ data = {}, type }) {
  const color = NODE_COLORS[type] || '#6b7280';
  return (
    <div className="bg-white border-2 rounded-lg shadow-sm min-w-[150px]" style={{ borderColor: color }}>
      <Handle type="target" position={Position.Top} style={{ background: color }} />
      <div className="px-3 py-1 text-xs font-bold text-white rounded-t-lg" style={{ backgroundColor: color }}>
        {NODE_LABELS[type] || type}
      </div>
      <div className="px-3 py-2 text-xs text-[#444444] max-w-[200px] truncate">
        {data.message || data.subject || data.title || data.label || data.tag || data.url
          || (data.minutes ? `${data.minutes}min` : '—')}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: color }} />
    </div>
  );
}

const nodeTypes = Object.fromEntries(
  Object.keys(NODE_COLORS).map(t => [t, (p) => <WFNode {...p} type={t} />])
);

const ACTION_TYPES = [
  'action_whatsapp', 'action_sms', 'action_email', 'action_webhook', 'action_ai_call',
  'action_tag', 'action_assign',
  'action_create_lead', 'action_assign_department', 'action_create_task', 'action_notify_user', 'action_create_support_ticket',
  'delay', 'condition', 'end',
];

let idCtr = 200;
const uid = () => `wn_${++idCtr}`;

const STATUS_BADGE = {
  running: 'bg-[#D6EAFB] text-[#0176D3]',
  completed: 'bg-[#E3F3E8] text-[#036B3A]',
  failed: 'bg-[#FDE7E9] text-[#BA0517]',
};

const FIELD_LABEL = "block text-xs font-medium text-[#706E6B] mb-1";
const FIELD_INPUT = "w-full border border-[#E5E5E5] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]";

// Every action node stores its config in `data` (Mixed on the backend), but
// until this panel existed nothing in the UI ever wrote into it — nodes were
// created with only `{ label }`, so a WhatsApp/Email/etc. step had no
// message to send. This is where that data actually gets set now.
function NodePropertiesPanel({ node, outgoingEdges, departments, users, onChange, onEdgeConditionChange, onClose }) {
  const { data = {}, type } = node;

  return (
    <div className="w-full lg:w-72 max-h-[50vh] lg:max-h-none flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-[#E5E5E5] p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#2E2E2E]">{NODE_LABELS[type] || type}</h3>
        <button onClick={onClose} className="text-[#706E6B] hover:text-[#5C5C5C] text-sm">✕</button>
      </div>

      {type === 'action_whatsapp' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Message</label>
          <textarea rows={5} value={data.message || ''} onChange={e => onChange({ message: e.target.value })}
            placeholder="Hi {{name}}, thanks for reaching out…" className={FIELD_INPUT} />
          <p className="text-[11px] text-[#706E6B]">Use {'{{name}}'}, {'{{phone}}'}, {'{{email}}'} to insert context values. Sent via the tenant's active WhatsApp channel.</p>
        </div>
      )}

      {type === 'action_sms' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Message</label>
          <textarea rows={5} value={data.message || ''} onChange={e => onChange({ message: e.target.value })} className={FIELD_INPUT} />
          <p className="text-[11px] text-[#DD7A01]">No SMS provider is configured yet — this step will fail until one is wired up.</p>
        </div>
      )}

      {type === 'action_email' && (
        <div className="space-y-3">
          <div>
            <label className={FIELD_LABEL}>Subject</label>
            <input value={data.subject || ''} onChange={e => onChange({ subject: e.target.value })} className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Body (HTML)</label>
            <textarea rows={6} value={data.body || ''} onChange={e => onChange({ body: e.target.value })} className={FIELD_INPUT} />
          </div>
        </div>
      )}

      {type === 'action_webhook' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>URL</label>
          <input value={data.url || ''} onChange={e => onChange({ url: e.target.value })}
            placeholder="https://example.com/hook" className={FIELD_INPUT} />
        </div>
      )}

      {type === 'action_tag' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Tag to add</label>
          <input value={data.tag || ''} onChange={e => onChange({ tag: e.target.value })} className={FIELD_INPUT} />
        </div>
      )}

      {type === 'action_assign' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Assign to (agent email)</label>
          <input value={data.agentId || ''} onChange={e => onChange({ agentId: e.target.value })} className={FIELD_INPUT} />
        </div>
      )}

      {type === 'action_create_lead' && (
        <div className="space-y-3">
          <p className="text-[11px] text-[#706E6B]">Creates (or reuses, if a matching phone/email already exists) a CRM lead from this run's context. Leave a field blank to fall back to the trigger's data.</p>
          <div>
            <label className={FIELD_LABEL}>Name</label>
            <input value={data.name || ''} onChange={e => onChange({ name: e.target.value })}
              placeholder="{{senderName}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Phone</label>
            <input value={data.phone || ''} onChange={e => onChange({ phone: e.target.value })}
              placeholder="{{phone}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Email</label>
            <input value={data.email || ''} onChange={e => onChange({ email: e.target.value })}
              placeholder="{{email}}" className={FIELD_INPUT} />
          </div>
        </div>
      )}

      {type === 'action_assign_department' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Department</label>
          <select value={data.departmentId || ''} onChange={e => onChange({ departmentId: e.target.value })} className={FIELD_INPUT}>
            <option value="">Select a department…</option>
            {departments.map(d => (
              <option key={d._id} value={d._id}>{d.name}</option>
            ))}
          </select>
          <p className="text-[11px] text-[#706E6B]">Requires a lead already in context (e.g. from a "Create Lead" step earlier in this workflow). Also assigns the department's default assignee, if one is set.</p>
        </div>
      )}

      {type === 'action_create_task' && (
        <div className="space-y-3">
          <div>
            <label className={FIELD_LABEL}>Title</label>
            <input value={data.title || ''} onChange={e => onChange({ title: e.target.value })}
              placeholder="Follow up with {{senderName}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Description</label>
            <textarea rows={3} value={data.description || ''} onChange={e => onChange({ description: e.target.value })} className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Due in (minutes)</label>
            <input type="number" min="0" value={data.dueInMinutes || ''} onChange={e => onChange({ dueInMinutes: e.target.value })} className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Assign to</label>
            <select value={data.assignedTo || ''} onChange={e => onChange({ assignedTo: e.target.value })} className={FIELD_INPUT}>
              <option value="">Unassigned</option>
              {users.map(u => (
                <option key={u._id} value={u._id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {type === 'action_notify_user' && (
        <div className="space-y-3">
          <div>
            <label className={FIELD_LABEL}>Notify</label>
            <select value={data.userId || ''} onChange={e => onChange({ userId: e.target.value })} className={FIELD_INPUT}>
              <option value="">Select a user…</option>
              {users.map(u => (
                <option key={u._id} value={u._id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={FIELD_LABEL}>Title</label>
            <input value={data.title || ''} onChange={e => onChange({ title: e.target.value })}
              placeholder="New enquiry" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Message</label>
            <textarea rows={3} value={data.message || ''} onChange={e => onChange({ message: e.target.value })}
              placeholder="New enquiry from {{senderName}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Link (optional)</label>
            <input value={data.link || ''} onChange={e => onChange({ link: e.target.value })} className={FIELD_INPUT} />
          </div>
        </div>
      )}

      {type === 'action_create_support_ticket' && (
        <div className="space-y-3">
          <div>
            <label className={FIELD_LABEL}>Subject</label>
            <input value={data.subject || ''} onChange={e => onChange({ subject: e.target.value })}
              placeholder="Enquiry from {{senderName}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Description</label>
            <textarea rows={4} value={data.description || ''} onChange={e => onChange({ description: e.target.value })}
              placeholder="{{text}}" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Priority</label>
            <select value={data.priority || 'medium'} onChange={e => onChange({ priority: e.target.value })} className={FIELD_INPUT}>
              {['low', 'medium', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={FIELD_LABEL}>Category</label>
            <input value={data.category || ''} onChange={e => onChange({ category: e.target.value })}
              placeholder="general" className={FIELD_INPUT} />
          </div>
        </div>
      )}

      {type === 'delay' && (
        <div className="space-y-1">
          <label className={FIELD_LABEL}>Wait (minutes)</label>
          <input type="number" min="0" value={data.minutes || ''} onChange={e => onChange({ minutes: e.target.value })} className={FIELD_INPUT} />
        </div>
      )}

      {type === 'condition' && (
        <div className="space-y-3">
          <div>
            <label className={FIELD_LABEL}>Field (from context)</label>
            <input value={data.field || ''} onChange={e => onChange({ field: e.target.value })}
              placeholder="e.g. status, tags, source" className={FIELD_INPUT} />
          </div>
          <div>
            <label className={FIELD_LABEL}>Operator</label>
            <select value={data.operator || 'equals'} onChange={e => onChange({ operator: e.target.value })} className={FIELD_INPUT}>
              {['equals', 'not_equals', 'contains', 'greater_than', 'less_than', 'exists'].map(op => (
                <option key={op} value={op}>{op.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          {data.operator !== 'exists' && (
            <div>
              <label className={FIELD_LABEL}>Value</label>
              <input value={data.value || ''} onChange={e => onChange({ value: e.target.value })} className={FIELD_INPUT} />
            </div>
          )}
          {outgoingEdges.length > 1 && (
            <div className="pt-2 border-t border-[#F3F2F2]">
              <label className={FIELD_LABEL}>Branch conditions</label>
              <p className="text-[11px] text-[#706E6B] mb-2">Each outgoing connection needs a True/False branch, or the workflow can't tell which path to take.</p>
              {outgoingEdges.map(e => (
                <div key={e.id} className="flex items-center justify-between mb-1.5 text-xs">
                  <span className="text-[#706E6B] truncate mr-2">→ {e.target}</span>
                  <select value={e.condition || ''} onChange={ev => onEdgeConditionChange(e.id, ev.target.value)}
                    className="border border-[#E5E5E5] rounded-lg px-1.5 py-1 text-xs">
                    <option value="">unset</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(type === 'trigger' || type === 'end' || type === 'action_ai_call') && (
        <p className="text-xs text-[#706E6B]">
          {type === 'action_ai_call' ? 'Places an AI-driven outbound call to the contact — no extra config needed.' : 'No configuration needed for this node.'}
        </p>
      )}
    </div>
  );
}

export default function WorkflowPage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('workflow_automation');
  const [workflows, setWorkflows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [wfName, setWfName] = useState('');
  const [triggerType, setTriggerType] = useState('new_lead');
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('canvas');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [triggerError, setTriggerError] = useState('');
  const [triggering, setTriggering] = useState(false);

  useEffect(() => {
    if (!hasAccess) return;
    api.get('/workflows').then(r => setWorkflows(r.data.data || []));
    api.get('/workflows/templates').then(r => setTemplates(r.data.data || []));
    api.get('/departments').then(r => setDepartments(r.data.departments || []));
    api.get('/auth/team-members').then(r => setUsers(r.data.teamMembers || r.data.users || []));
  }, [hasAccess]);

  // These must stay above the feature-gate early returns below — hooks
  // can't be called conditionally, and this component used to return early
  // (loading / no access) before reaching them, changing the hook count
  // between renders and crashing React with "Rendered more hooks than
  // during the previous render."
  const onNodesChange = useCallback(c => setNodes(ns => applyNodeChanges(c, ns)), []);
  const onEdgesChange = useCallback(c => setEdges(es => applyEdgeChanges(c, es)), []);
  const onConnect = useCallback(conn => setEdges(es => addEdge({ ...conn, id: `e_${Date.now()}` }, es)), []);
  const onNodeClick = useCallback((_e, node) => setSelectedNodeId(node.id), []);
  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Workflow Automation"
        subtitle="Visual trigger-to-action automation across every module"
        icon={Workflow}
        plan={plan}
        featureName="Workflow Automation"
        description="Upgrade to unlock the visual automation builder — chain triggers, conditions, and actions across WhatsApp, email, and your CRM."
        highlights={[
          { icon: Zap, label: 'Instant triggers', desc: 'New lead, missed call, tag added, and more' },
          { icon: GitBranch, label: 'Branching logic', desc: 'Conditions and delays between every step' },
          { icon: Workflow, label: 'Ready templates', desc: 'Use proven automations in one click' },
        ]}
      />
    );
  }

  const loadWorkflow = async (id) => {
    const { data } = await api.get(`/workflows/${id}`);
    const wf = data.data;
    setActiveId(wf._id); setWfName(wf.name); setTriggerType(wf.triggerType);
    setNodes(wf.nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })));
    setEdges(wf.edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label, condition: e.condition })));
    const runs = await api.get(`/workflows/${id}/runs`);
    setRuns(runs.data.data || []);
    setTab('canvas');
  };

  const newWorkflow = () => {
    setActiveId(null); setWfName('New Workflow'); setTriggerType('new_lead');
    setNodes([{ id: 'trigger_1', type: 'trigger', position: { x: 250, y: 50 }, data: { label: 'Trigger' } }]);
    setEdges([]); setRuns([]);
  };

  const createFromTemplate = async (idx, name) => {
    const { data } = await api.post('/workflows/templates/create', { templateIndex: idx, name });
    setWorkflows(ws => [...ws, data.data]);
    loadWorkflow(data.data._id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: wfName, triggerType,
        nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label, condition: e.condition })),
      };
      if (activeId) {
        await api.put(`/workflows/${activeId}`, payload);
      } else {
        const { data } = await api.post('/workflows', payload);
        setActiveId(data.data._id);
        setWorkflows(ws => [...ws, data.data]);
      }
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    await api.patch(`/workflows/${id}/toggle`);
    const { data } = await api.get(`/workflows/${id}`);
    setWorkflows(ws => ws.map(w => w._id === id ? data.data : w));
    if (activeId === id) setNodes(n => [...n]); // force re-render
  };

  const triggerManual = async () => {
    if (!activeId) return;
    if (!activeWf?.isActive) {
      setTriggerError('Activate this workflow before running it — a paused workflow will not execute.');
      return;
    }
    setTriggering(true);
    setTriggerError('');
    try {
      // A test run needs a sample context or every action step (which reads
      // context.phone/email/leadId) silently no-ops while still reporting
      // "done" — giving a false impression the test actually did something.
      await api.post(`/workflows/${activeId}/trigger`, {
        phone: '910000000000',
        email: 'test@example.com',
        leadId: null,
      });
      setTimeout(() => api.get(`/workflows/${activeId}/runs`).then(r => setRuns(r.data.data || [])), 2000);
      setTab('runs');
    } catch (err) {
      setTriggerError(err.response?.data?.error || 'Failed to trigger workflow');
    } finally {
      setTriggering(false);
    }
  };

  const addNode = (type) => {
    const id = uid();
    setNodes(ns => [...ns, {
      id, type,
      position: { x: 100 + Math.random() * 300, y: 200 + Math.random() * 200 },
      data: { label: NODE_LABELS[type] },
    }]);
    setSelectedNodeId(id);
  };

  const updateNodeData = (patch) => {
    setNodes(ns => ns.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, ...patch } } : n));
  };

  const updateEdgeCondition = (edgeId, condition) => {
    setEdges(es => es.map(e => e.id === edgeId ? { ...e, condition, label: condition } : e));
  };

  const activeWf = workflows.find(w => w._id === activeId);
  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const selectedNodeOutgoingEdges = selectedNode ? edges.filter(e => e.source === selectedNode.id) : [];

  return (
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden bg-[#F7F9FB]">
      {/* Sidebar */}
      <div className="w-full lg:w-56 max-h-[40vh] lg:max-h-none flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E5E5E5] flex flex-col">
        <div className="p-4 border-b border-[#E5E5E5]">
          <h2 className="text-sm font-bold text-[#2E2E2E] mb-2">Workflows</h2>
          <button onClick={newWorkflow} className="w-full bg-[#0176D3] text-white text-xs py-1.5 rounded hover:bg-[#014486]">+ New</button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {workflows.map(w => (
            <div key={w._id} onClick={() => loadWorkflow(w._id)}
              className={`p-2 rounded-lg cursor-pointer mb-1 text-sm ${w._id === activeId ? 'bg-[#EAF4FE] text-[#0176D3] font-medium' : 'hover:bg-[#F3F2F2] text-[#444444]'}`}>
              <div className="flex items-center justify-between">
                <span className="truncate text-xs">{w.name}</span>
                {w.isActive && <span className="text-[#04844B] text-xs">●</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-[#E5E5E5]">
          <p className="text-xs font-medium text-[#706E6B] mb-1">Add Step</p>
          {ACTION_TYPES.map(t => (
            <button key={t} onClick={() => addNode(t)}
              className="w-full text-left text-xs px-2 py-1 rounded hover:bg-[#F3F2F2] text-[#5C5C5C] capitalize mb-0.5"
              style={{ borderLeft: `3px solid ${NODE_COLORS[t]}` }}>
              {NODE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b border-[#E5E5E5] px-4 py-2 flex items-center gap-3 flex-wrap">
          <input value={wfName} onChange={e => setWfName(e.target.value)}
            className="text-sm font-medium border border-[#D8DDE6] rounded px-3 py-1.5 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
          <select value={triggerType} onChange={e => setTriggerType(e.target.value)}
            className="border border-[#D8DDE6] rounded px-3 py-1.5 text-sm focus:outline-none">
            {[
              'new_lead', 'tag_added', 'status_changed', 'missed_call', 'call_completed', 'form_submitted', 'manual',
              'whatsapp_message', 'instagram_message', 'facebook_message', 'support_widget_message', 'email_message', 'telegram_message',
              'deal_stage_changed', 'deal_won', 'deal_lost', 'task_completed', 'task_overdue', 'lead_quiet', 'deal_rotten',
              'payment_received', 'invoice_paid', 'invoice_overdue', 'quotation_accepted',
            ].map(t => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <button onClick={save} disabled={saving}
            className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-4 py-1.5 rounded text-[13px] font-semibold whitespace-nowrap">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {activeId && (
            <>
              <button onClick={() => toggle(activeId)}
                className={`text-sm px-3 py-1.5 rounded border  whitespace-nowrap ${activeWf?.isActive ? 'border-[#FFB75D] text-[#8C4B02]' : 'border-[#9FD9B4] text-[#036B3A]'}`}>
                {activeWf?.isActive ? 'Pause' : 'Activate'}
              </button>
              <button onClick={triggerManual} disabled={triggering || !activeWf?.isActive}
                title={!activeWf?.isActive ? 'Activate this workflow first' : 'Run once with sample test data'}
                className="text-sm px-3 py-1.5 rounded border border-[#E5E5E5] text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                {triggering ? 'Running…' : '▶ Test Run'}
              </button>
            </>
          )}
          <div className="ml-auto flex gap-1 overflow-x-auto">
            {['canvas', 'templates', 'runs'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`text-xs px-3 py-1.5 rounded whitespace-nowrap shrink-0 ${tab === t ? 'bg-[#2E2E2E] text-white' : 'bg-[#F3F2F2] text-[#5C5C5C] hover:bg-[#E5E5E5]'} capitalize`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {triggerError && (
          <div className="bg-[#FDE7E9] border-b border-[#F6A3AB] text-[#8E030F] text-xs px-4 py-2 flex items-center justify-between">
            <span>{triggerError}</span>
            <button onClick={() => setTriggerError('')} className="text-[#BA0517] hover:text-[#BA0517] ml-3">✕</button>
          </div>
        )}

        {/* Canvas */}
        {tab === 'canvas' && (
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            <div className="flex-1">
              <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
                onNodeClick={onNodeClick} onPaneClick={onPaneClick} fitView>
                <Background /> <Controls /> <MiniMap />
              </ReactFlow>
            </div>
            {selectedNode && (
              <NodePropertiesPanel
                node={selectedNode}
                outgoingEdges={selectedNodeOutgoingEdges}
                departments={departments}
                users={users}
                onChange={updateNodeData}
                onEdgeConditionChange={updateEdgeCondition}
                onClose={() => setSelectedNodeId(null)}
              />
            )}
          </div>
        )}

        {/* Templates */}
        {tab === 'templates' && (
          <div className="flex-1 overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-[#080707] mb-4">Pre-Built Templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {templates.map((t, i) => (
                <div key={i} className="bg-white border border-[#E5E5E5] rounded-lg p-4 hover:border-[#B8DAF3] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <h3 className="font-semibold text-[#2E2E2E] mb-1">{t.name}</h3>
                  <p className="text-xs text-[#706E6B] mb-3 leading-relaxed">{t.description}</p>
                  <p className="text-xs text-[#706E6B] mb-3">Trigger: <span className="font-medium text-[#5C5C5C]">{t.triggerType?.replace(/_/g, ' ')}</span> · {t.nodes?.length} nodes</p>
                  <button onClick={() => createFromTemplate(i, t.name)}
                    className="w-full bg-[#0176D3] hover:bg-[#014486] text-white text-xs py-2 rounded font-medium">
                    Use Template
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Runs */}
        {tab === 'runs' && (
          <div className="flex-1 overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-[#080707] mb-4">Recent Runs</h2>
            {runs.length === 0 && <p className="text-[#706E6B] text-sm">No runs yet. Click "Test Run" to trigger manually.</p>}
            <div className="space-y-3">
              {runs.map(r => (
                <div key={r._id} className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                    <span className="text-xs text-[#706E6B]">{new Date(r.createdAt).toLocaleString()}</span>
                    <span className="text-xs text-[#706E6B]">{r.steps?.length || 0} steps</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(r.steps || []).map((s, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded-lg ${s.status === 'done' ? 'bg-[#E3F3E8] text-[#036B3A]' : s.status === 'failed' ? 'bg-[#FDE7E9] text-[#BA0517]' : 'bg-[#F3F2F2] text-[#706E6B]'}`}>
                        {NODE_LABELS[s.nodeType] || s.nodeType}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
